import React from 'react'
// libs
import { change } from 'redux-form'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// hooks
import { useModalData } from 'hooks/useShowModal'
// components
import { Modal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
// view components
import Result from './components/Result'
import AiPrompt from 'components/Modals/AiFieldValueModal/components/AiPrompt'
import AiResultActions from 'components/Modals/AiFieldValueModal/components/AiResultActions'
// constants
import { Forms } from 'constants/forms'
import { Modals } from 'constants/modals'
import { IField } from 'types/graphql'
// graphql
import { REQUEST_AI_FIELD_FILL_MUTATION } from 'graphql/risk/mutations'
// views
import { Container, Header } from './views'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// utils
import { handleError } from 'utils/errors/handlers'
import { getFieldValue, getDisplayValue } from './utils'

type ModalData = {
  field: IField
  handleConfirm?: () => void
}

type Step = 'prompt' | 'result'

const AiFieldValueModal: React.FC = () => {
  const dispatch = useDispatch()

  const { risk } = React.useContext(RiskAPIContext)

  const {
    hideModal,
    modalData: { isOpen = false, field, handleConfirm }
  } = useModalData<ModalData>({ modalType: Modals.AI_FIELD_VALUE_MODAL })

  const [requestAiFieldFill] = useMutation<any>(REQUEST_AI_FIELD_FILL_MUTATION)

  const [step, setStep] = React.useState<Step>('prompt')
  const [result, setResult] = React.useState<{ displayValue: string; value: any; explanation: string } | null>(null)
  const [loading, setLoading] = React.useState(false)

  const abortControllerRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => {
    if (!isOpen) {
      abortControllerRef.current?.abort()
      setStep('prompt')
      setResult(null)
    }
  }, [isOpen])

  const generateFieldValue = async (prompt: string) => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    setLoading(true)

    try {
      const {
        data: { requestAiFieldFill: data }
      } = await requestAiFieldFill({
        variables: {
          input: {
            fieldId: field.id,
            userMessage: prompt,
            contextRecordId: risk.id
          }
        },
        context: { fetchOptions: { signal: controller.signal } }
      })

      setResult({
        ...data,
        displayValue: getDisplayValue(data.value, field)
      })
      setStep('result')
    } catch (e) {
      if (!controller.signal.aborted) {
        handleError(e, 'AiFieldValueModal.generateFieldValue')
      }
    } finally {
      setLoading(false)
      abortControllerRef.current = null
    }
  }

  const onApplyClick = () => {
    if (result) {
      dispatch(change(Forms.RISK_TEMPLATE_FORM, `${field.name}.value`, getFieldValue(result, field)))
      handleConfirm?.()
    }
    hideModal()
  }

  const onTryAgain = () => {
    setStep('prompt')
    setResult(null)
  }

  const onCancel = () => {
    abortControllerRef.current?.abort()
    hideModal()
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal isOpen={isOpen} width='sm' hideModal={onCancel}>
      <Container>
        <Header>
          Generate a value {field ? ` for ${field.label}` : ''}
          <CloseIcon onClick={onCancel} />
        </Header>
        {loading ? (
          <AiResultActions loading onApply={onApplyClick} onTryAgain={onTryAgain} onCancel={onCancel} />
        ) : step === 'result' && result ? (
          <Result
            onApply={onApplyClick}
            onTryAgain={onTryAgain}
            value={result.displayValue}
            explanation={result.explanation}
          />
        ) : (
          <AiPrompt
            required
            onCancel={onCancel}
            onConfirm={generateFieldValue}
            suggestedPrompts={field?.aiSuggestedPrompts}
            description='Describe what you want rAI to generate for this field (optional)'
            placeholder='e.g. Generate a concise risk description for a financial compliance process'
          />
        )}
      </Container>
    </Modal>
  )
}

export default AiFieldValueModal
